// ABC SEO/GEO — Agent Readiness.
//
// Machine discoverability: can an AI agent find, read and act on this business?
// Separate from the GEO screen on purpose — GEO measures whether AI engines
// MENTION you, this measures whether they can CONSUME you. Folding them into
// one number would also have moved every existing client's GEO score the day
// it shipped, which is the trust problem the platform already learned once.
//
// THE TWO NUMBERS ARE THE POINT.
// Cloudflare publishes a free scanner at isitagentready.com. A client can grade
// themselves in ten seconds. If our number is kinder than theirs and we never
// mention it, we look wrong — so we show both, label both, and explain the gap:
// their scan counts eight API and authentication checks against a brochure site
// that has no API. Ours drops what does not apply. Same evidence, two readings.
(function () {
  const { Icon } = window.SGIcons;
  const { Card, CardHead, PageHead, Gauge } = window.SGKit;

  // Mirrors the public scanner's own sections so a client can read the two
  // reports side by side without translating between them.
  const CATEGORIES = [
    { id: 'discoverability', label: 'Discoverability', icon: 'search' },
    { id: 'content', label: 'Content', icon: 'file-text' },
    { id: 'bot_access', label: 'Bot Access Control', icon: 'lock' },
    { id: 'api_auth_mcp', label: 'API, Auth, MCP & Skills', icon: 'key' },
  ];

  const STATUS = {
    pass: { label: 'In place', color: 'var(--sg-green, #16A34A)', bg: 'var(--sg-green-50, #F0FDF4)', icon: 'check' },
    fail: { label: 'Missing', color: 'var(--sg-red, #DC2626)', bg: 'var(--sg-red-50, #FEF2F2)', icon: 'x' },
    not_applicable: { label: 'Not applicable', color: 'var(--sg-text-3)', bg: 'var(--sg-bg-2, #F8FAFC)', icon: 'minus' },
    could_not_check: { label: 'Could not check', color: 'var(--sg-amber, #D97706)', bg: 'var(--sg-amber-50, #FFFBEB)', icon: 'alert-triangle' },
  };

  function StatusPill({ status }) {
    const s = STATUS[status] || STATUS.fail;
    return (
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, flex: 'none', padding: '3px 10px', borderRadius: 'var(--sg-radius-pill)', background: s.bg, color: s.color, fontSize: 11.5, fontWeight: 700, whiteSpace: 'nowrap' }}>
        <Icon name={s.icon} size={11} /> {s.label}
      </span>
    );
  }

  function CheckRow({ check }) {
    const [open, setOpen] = React.useState(false);
    const s = STATUS[check.status] || STATUS.fail;
    const canOpen = !!(check.recommendation || check.evidence);
    return (
      <div style={{ borderTop: '1px solid var(--sg-border)', padding: '13px 0' }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
          <span style={{ flex: 'none', width: 8, height: 8, borderRadius: '50%', background: s.color, marginTop: 6 }} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              <span style={{ fontWeight: 700, fontSize: 13.5, color: 'var(--sg-text)' }}>{check.name}</span>
              {check.advisory && (
                <span title="Reported, but never counted against your score — it rests on a draft standard that has not been adopted."
                  style={{ fontSize: 10.5, fontWeight: 700, padding: '2px 7px', borderRadius: 'var(--sg-radius-pill)', background: 'var(--sg-violet-50, #F5F3FF)', color: 'var(--sg-violet-600)' }}>Advisory</span>
              )}
              {!check.inExternalScan && (
                <span title="The public Cloudflare scan does not check this. It still changes what AI assistants read."
                  style={{ fontSize: 10.5, fontWeight: 700, padding: '2px 7px', borderRadius: 'var(--sg-radius-pill)', background: 'var(--sg-bg-2, #F8FAFC)', color: 'var(--sg-text-3)' }}>Beyond the public scan</span>
              )}
            </div>
            <div style={{ fontSize: 12.5, color: 'var(--sg-text-2)', lineHeight: 1.55, marginTop: 3 }}>{check.detail}</div>
            {open && (
              <div style={{ marginTop: 9, paddingLeft: 12, borderLeft: '2px solid var(--sg-border)' }}>
                {check.evidence && (
                  <div style={{ fontSize: 11.5, color: 'var(--sg-text-3)', fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', marginBottom: check.recommendation ? 7 : 0, wordBreak: 'break-word' }}>
                    {check.evidence}
                  </div>
                )}
                {check.recommendation && (
                  <div style={{ fontSize: 12.5, color: 'var(--sg-text)', lineHeight: 1.6 }}>{check.recommendation}</div>
                )}
              </div>
            )}
            {canOpen && (
              <button onClick={() => setOpen(!open)}
                style={{ marginTop: 6, background: 'none', border: 'none', padding: 0, cursor: 'pointer', fontFamily: 'var(--sg-font-sans)', fontSize: 12, fontWeight: 700, color: 'var(--sg-blue-600)' }}>
                {open ? 'Hide detail' : check.recommendation ? 'How to fix' : 'Show evidence'}
              </button>
            )}
          </div>
          <StatusPill status={check.status} />
        </div>
      </div>
    );
  }

  function AgentReadiness() {
    const { site, prettySite } = window.SGLive.useLive();
    const domain = site ? prettySite(site) : '';
    const [data, setData] = React.useState(null);
    const [loading, setLoading] = React.useState(false);
    // Declared, never guessed: most clients are brochure sites with no API, and
    // auto-failing them against RFC 9727 would sell a fix for a non-problem.
    const [hasApi, setHasApi] = React.useState(false);

    const load = React.useCallback(() => {
      setLoading(true);
      setData(null);
      window.SGLive.fetchAgentReadiness(site || '', hasApi)
        .then((d) => setData(d))
        .finally(() => setLoading(false));
    }, [site, hasApi]);

    React.useEffect(() => { load(); }, [load]);

    const checks = (data && data.checks) || [];
    const byCat = (id) => checks.filter((c) => c.category === id);

    return (
      <div>
        <PageHead
          eyebrow="MACHINE DISCOVERABILITY"
          eyebrowColor="var(--sg-violet-600)"
          title="Agent Readiness"
          sub={domain ? `Can AI agents find, read and act on ${domain}?` : 'Can AI agents find, read and act on this business?'}
          icon="globe"
          iconGrad="var(--sg-grad-violet)"
          right={
            <button onClick={load} disabled={loading}
              style={{ display: 'inline-flex', alignItems: 'center', gap: 7, height: 38, padding: '0 16px', border: 'none', borderRadius: 'var(--sg-radius-pill)', cursor: loading ? 'default' : 'pointer', opacity: loading ? 0.6 : 1, fontFamily: 'var(--sg-font-sans)', fontWeight: 700, fontSize: 13, color: '#fff', background: 'var(--sg-grad-violet)' }}>
              <Icon name="refresh-cw" size={14} /> {loading ? 'Checking…' : 'Re-check'}
            </button>
          }
        />

        {loading && (
          <Card style={{ padding: 34, textAlign: 'center', color: 'var(--sg-text-2)', fontSize: 13.5 }}>
            Running 18 checks across the site, its DNS and its well-known files…
          </Card>
        )}

        {!loading && !data && (
          <Card style={{ padding: 34, textAlign: 'center' }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--sg-text)', marginBottom: 6 }}>Nothing to show yet</div>
            <div style={{ fontSize: 13, color: 'var(--sg-text-2)' }}>
              We could not reach that site to check it. Confirm the domain in Settings, then re-check.
            </div>
          </Card>
        )}

        {!loading && data && (
          <>
            {/* The two scores, side by side and labelled. Never one without the other. */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 16, marginBottom: 16 }}>
              <Card style={{ padding: 22, textAlign: 'center' }}>
                <div style={{ fontSize: 11.5, fontWeight: 700, letterSpacing: '.07em', textTransform: 'uppercase', color: 'var(--sg-text-3)', marginBottom: 12 }}>
                  Applies to this business
                </div>
                <Gauge value={data.score == null ? 0 : data.score} color="var(--sg-violet-600)" big />
                <div style={{ fontSize: 12.5, color: 'var(--sg-text-2)', marginTop: 12, lineHeight: 1.55 }}>
                  {data.passed} of {data.applicable} applicable checks in place. Checks that do not apply to
                  this business are left out entirely, rather than counted against it.
                </div>
              </Card>

              <Card style={{ padding: 22, textAlign: 'center' }}>
                <div style={{ fontSize: 11.5, fontWeight: 700, letterSpacing: '.07em', textTransform: 'uppercase', color: 'var(--sg-text-3)', marginBottom: 12 }}>
                  Public Cloudflare scan will show
                </div>
                <Gauge value={data.externalScore} color="var(--sg-blue-500)" big />
                <div style={{ fontSize: 12.5, color: 'var(--sg-text-2)', marginTop: 12, lineHeight: 1.55 }}>
                  {data.externalPassed} of {data.externalTotal}. That scanner weights every check equally and
                  counts “no API” as a failure — so this number reads lower. Expect to see it if you scan yourself.
                </div>
              </Card>
            </div>

            {/* Explain the gap in words, once, where a client will actually read it. */}
            {data.score != null && data.externalScore !== data.score && (
              <Card style={{ padding: '15px 18px', marginBottom: 16, display: 'flex', gap: 11, alignItems: 'flex-start' }}>
                <Icon name="info" size={17} color="var(--sg-blue-600)" />
                <div style={{ fontSize: 13, color: 'var(--sg-text-2)', lineHeight: 1.6 }}>
                  <strong style={{ color: 'var(--sg-text)' }}>Why the two numbers differ.</strong>{' '}
                  The public scan grades every site against the same list, including API catalogues and OAuth
                  metadata that only apply to a business that publishes an API. Where a check genuinely does not
                  apply here, we leave it out instead of marking it failed — publishing those files anyway would
                  point agents at documentation that does not exist.
                </div>
              </Card>
            )}

            {data.incomplete && (
              <Card style={{ padding: '13px 18px', marginBottom: 16, display: 'flex', gap: 11, alignItems: 'center' }}>
                <Icon name="alert-triangle" size={16} color="var(--sg-amber, #D97706)" />
                <div style={{ fontSize: 12.5, color: 'var(--sg-text-2)' }}>
                  Some checks could not be completed. Those are shown as “could not check” and are left out of
                  both scores — never reported as failures.
                </div>
              </Card>
            )}

            {/* Does this business publish an API? Declared by a human. */}
            <Card style={{ padding: '14px 18px', marginBottom: 16, display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
              <label style={{ display: 'inline-flex', alignItems: 'center', gap: 9, cursor: 'pointer', fontSize: 13, color: 'var(--sg-text)' }}>
                <input type="checkbox" checked={hasApi} onChange={(e) => setHasApi(e.target.checked)} style={{ width: 16, height: 16, cursor: 'pointer' }} />
                <span style={{ fontWeight: 700 }}>This business publishes an API</span>
              </label>
              <span style={{ fontSize: 12.5, color: 'var(--sg-text-3)', flex: 1, minWidth: 220 }}>
                Leave unticked for a normal business site. Ticking it turns on the API-catalogue and
                authentication checks, which only make sense when there is an API to describe.
              </span>
            </Card>

            {CATEGORIES.map((cat) => {
              const rows = byCat(cat.id);
              if (!rows.length) return null;
              const ext = rows.filter((r) => r.inExternalScan);
              const extPass = ext.filter((r) => r.status === 'pass').length;
              return (
                <Card key={cat.id} style={{ marginBottom: 16 }}>
                  <CardHead
                    title={cat.label}
                    icon={cat.icon}
                    sub={ext.length ? `Public scan counts ${extPass} of ${ext.length} here` : 'Not part of the public scan'}
                  />
                  <div style={{ padding: '0 18px 6px' }}>
                    {rows.map((c) => <CheckRow key={c.id} check={c} />)}
                  </div>
                </Card>
              );
            })}
          </>
        )}
      </div>
    );
  }

  window.SGAgentReadiness = AgentReadiness;
})();
